import { Alert, Grid } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { Select } from '@/components/Form/Select';
import { Icon } from '@/components/Icon/Icon';

import { LANGUAGES } from '@/constants/countries';
import { getTenantConfigs } from '@/constants/tenant-configs';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectTerritories } from '@/redux/countries';

import { Language } from '@/types/i18n';

import {
  getLanguageFromAvailable,
  getTenantForCountryCode,
} from '@/utils/languages';
import { isSupportedLanguage } from '@/utils/translations';

type LanguageSelectProps = {
  disabled: boolean;
};

export const LanguageSelect = ({ disabled }: LanguageSelectProps) => {
  const { t, switchLanguage, i18n } = useTranslate();
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
    trigger,
  } = useFormContext();
  const { territoryByCountryCode } = useAppSelector(selectTerritories);
  const countryCode = watch('countryCode');
  const language = watch('language');

  const selectedTenant = useMemo(
    () => getTenantForCountryCode(territoryByCountryCode, countryCode),
    [countryCode, territoryByCountryCode],
  );

  const availableForCountry = useMemo(() => {
    if (!countryCode || !selectedTenant) {
      return [Language.English];
    }
    return (
      getTenantConfigs(selectedTenant)?.availableLanguages ?? [Language.English]
    );
  }, [countryCode, selectedTenant]);

  const languageOptions = useMemo(() => {
    return availableForCountry.length > 1
      ? availableForCountry
          .map(lang => LANGUAGES.find(l => l.value === lang))
          .filter(Boolean)
      : [];
  }, [availableForCountry]);

  const hasLanguages = languageOptions.length > 0;

  useEffect(() => {
    if (!countryCode || !selectedTenant) {
      return;
    }
    const preferredSource = isSupportedLanguage(i18n.language)
      ? i18n.language
      : null;
    const preferredLanguage = getLanguageFromAvailable(
      availableForCountry,
      preferredSource,
    );
    if (language !== preferredLanguage) {
      setValue('language', preferredLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode, selectedTenant, availableForCountry, i18n.language]);

  useEffect(() => {
    if (!language || !countryCode || !selectedTenant) {
      return;
    }
    const resolvedLanguage = getLanguageFromAvailable(
      availableForCountry,
      language,
    );
    if (resolvedLanguage === language) {
      void switchLanguage(resolvedLanguage, selectedTenant || undefined);
    } else {
      setValue('language', resolvedLanguage);
    }

    if (errors) {
      const fieldsWithError = Object.keys(errors);
      clearErrors();

      setTimeout(() => {
        fieldsWithError.forEach(field => trigger(field));
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, countryCode, availableForCountry, selectedTenant]);

  if (!hasLanguages) {
    return null;
  }

  return (
    <>
      <Grid item xs={12}>
        <Select
          options={languageOptions as { value: Language; text: string }[]}
          control={control}
          name="language"
          label={t('countrySelect.form.language.label')}
          error={!!errors.language}
          helperText={errors.language?.message as string}
          rules={{
            required: t('countrySelect.form.language.error.required'),
          }}
          fullWidth
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <Alert icon={<Icon name="info" />} severity="info" variant="outlined">
          {t('countrySelect.form.language.info')}
        </Alert>
      </Grid>
    </>
  );
};
